import {
  MattersStyleTableShell,
  useMattersStyleTableColumnDragSensors,
} from "@/components/features/matters/matters-style-table/MattersStyleTableShell";
import {
  isNotificationAction,
  NotificationCatalogBulkActions,
  PermissionCatalogGrants,
  PermissionCatalogSection,
} from "@/components/features/settings/permission-groups/PermissionCatalogSections";
import { normalizeInferredPageGrants } from "@/components/features/settings/permission-groups/PermissionPageAccordion";
import {
  ActionConfirmationModal,
  AppModal,
  Button,
  Form,
  FormSubmitButton,
  Text,
  TextFormField,
  Textarea,
} from "@/components/ui";
import { FormCancelButton } from "@/components/ui/FormFields/FormCancelButton/FormCancelButton";
import { Accordion } from "@/components/ui/shadcn/Accordion/Accordion";
import {
  Tabs,
  TabsContent,
  TabsList,
  TabsTrigger,
} from "@/components/ui/shadcn/Tabs/Tabs";
import {
  getPermissionCatalogQueryOptions,
  getPermissionGroupsQueryOptions,
  useCreatePermissionGroup,
  useDeletePermissionGroup,
  useUpdatePermissionGroup,
} from "@/lib/data/react-query";
import { serverErrorFallbackText } from "@/lib/data/static/fallbackMessages";
import { PermissionGrantSchema } from "@/lib/data/schemas";
import { tanstackTableNames } from "@/lib/data/static/tanstack-table-names";
import {
  type PermissionGroupAction,
  type PermissionCatalogDefinition,
  type PermissionGroup,
  type PermissionGroupWithUsers,
} from "@/lib/types";
import {
  customDateTimeSortingFns,
  MultiValueCell,
} from "@/lib/utils/tableUtils";
import { zodResolver } from "@hookform/resolvers/zod";
import { arrayMove } from "@dnd-kit/sortable";
import type { DragEndEvent } from "@dnd-kit/core";
import {
  type ColumnDef,
  functionalUpdate,
  getCoreRowModel,
  getPaginationRowModel,
  getSortedRowModel,
  type SortingState,
  useReactTable,
} from "@tanstack/react-table";
import { useQuery } from "@tanstack/react-query";
import { useEffect, useMemo, useRef, useState } from "react";
import { useForm } from "react-hook-form";
import { z } from "zod";

const FORM_ID = "permission-group-form";
const columnIds = ["Label", "Description", "Pages", "Actions", "Users"];
const formSchema = z.object({
  label: z.string().trim().min(1, "Label is required"),
  description: z.string(),
  grants: z.array(PermissionGrantSchema),
});
type FormValues = z.infer<typeof formSchema>;

export function PermissionGroupsView({
  canUpdate,
  canDelete,
  isAdding,
  setIsAdding,
}: {
  canUpdate: boolean;
  canDelete: boolean;
  isAdding: boolean;
  setIsAdding: (open: boolean) => void;
}) {
  const [activeGroup, setActiveGroup] = useState<PermissionGroup | null>(null);
  const [isEditing, setIsEditing] = useState(false);
  const [pendingDelete, setPendingDelete] = useState<PermissionGroup | null>(
    null,
  );
  const [search, setSearch] = useState("");
  const [sorting, setSorting] = useState<SortingState>([]);
  const [pagination, setPagination] = useState({ pageIndex: 0, pageSize: 10 });
  const [columnOrder, setColumnOrder] = useState<string[]>(columnIds);
  const {
    data: groupsData,
    isLoading,
    isError,
  } = useQuery(getPermissionGroupsQueryOptions());
  const { data: catalogData } = useQuery(getPermissionCatalogQueryOptions());
  const deleteGroup = useDeletePermissionGroup();
  const groups = useMemo(() => groupsData?.data ?? [], [groupsData?.data]);
  const catalog = useMemo(
    () =>
      [...(catalogData?.data ?? [])]
        .sort(
          (left, right) =>
            left.displayOrder - right.displayOrder ||
            left.id.localeCompare(right.id),
        )
        .map((permission) => ({
          ...permission,
          actions: [...permission.actions].sort(
            (left, right) =>
              left.displayOrder - right.displayOrder ||
              left.action.localeCompare(right.action),
          ),
        })),
    [catalogData?.data],
  );
  const permissionById = useMemo(
    () => new Map(catalog.map((permission) => [permission.id, permission])),
    [catalog],
  );
  const parentPageIds = useMemo(
    () =>
      new Set(catalog.flatMap((permission) => permission.parentPermissionIds)),
    [catalog],
  );
  const rows = useMemo(() => {
    const value = search.trim().toLowerCase();
    return groups.filter(
      (group) =>
        !value ||
        group.label.toLowerCase().includes(value) ||
        (group.description ?? "").toLowerCase().includes(value),
    );
  }, [groups, search]);
  const columns = useMemo<ColumnDef<PermissionGroupWithUsers>[]>(
    () => [
      {
        id: "Label",
        accessorFn: (group) => group.label,
        header: "Label",
        cell: ({ row }) => <Text variant="body">{row.original.label}</Text>,
      },
      {
        id: "Description",
        accessorFn: (group) => group.description ?? "",
        header: "Description",
        cell: ({ row }) => (
          <Text variant="subHeading">{row.original.description || "-"}</Text>
        ),
      },
      {
        id: "Pages",
        accessorFn: (group) =>
          new Set(
            group.grants
              .filter(
                (grant) =>
                  permissionById.get(grant.permissionId)?.kind === "page" &&
                  !parentPageIds.has(grant.permissionId),
              )
              .map((grant) => grant.permissionId),
          ).size,
        header: "Pages",
        cell: ({ row }) => (
          <MultiValueCell
            value={Array.from(
              new Set(
                row.original.grants
                  .filter(
                    (grant) =>
                      permissionById.get(grant.permissionId)?.kind === "page" &&
                      !parentPageIds.has(grant.permissionId),
                  )
                  .map(
                    (grant) =>
                      permissionById.get(grant.permissionId)?.label ??
                      grant.permissionId,
                  ),
              ),
            )}
            showItemCount={3}
          />
        ),
      },
      {
        id: "Actions",
        accessorFn: (group) =>
          new Set(
            group.grants
              .filter(
                (grant) =>
                  permissionById.get(grant.permissionId)?.kind === "object",
              )
              .map((grant) => grant.permissionId),
          ).size,
        header: "Actions",
        cell: ({ row }) => (
          <MultiValueCell
            value={Array.from(
              new Set(
                row.original.grants
                  .filter(
                    (grant) =>
                      permissionById.get(grant.permissionId)?.kind === "object",
                  )
                  .map(
                    (grant) =>
                      permissionById.get(grant.permissionId)?.label ??
                      grant.permissionId,
                  ),
              ),
            )}
            showItemCount={3}
          />
        ),
      },
      {
        id: "Users",
        accessorFn: (group) => group.users.length,
        header: "Users",
        cell: ({ row }) => (
          <Text variant="subHeading">{row.original.users.length}</Text>
        ),
      },
    ],
    [parentPageIds, permissionById],
  );
  const table = useReactTable({
    data: rows,
    columns,
    state: { sorting, pagination, columnOrder },
    onSortingChange: (updater) => {
      setSorting((old) => functionalUpdate(updater, old));
    },
    onPaginationChange: (updater) => {
      setPagination((old) => functionalUpdate(updater, old));
    },
    onColumnOrderChange: (updater) => {
      setColumnOrder((old) => functionalUpdate(updater, old));
    },
    getCoreRowModel: getCoreRowModel(),
    getSortedRowModel: getSortedRowModel(),
    getPaginationRowModel: getPaginationRowModel(),
    sortingFns: customDateTimeSortingFns,
  });
  const sensors = useMattersStyleTableColumnDragSensors();
  const openGroup = (group: PermissionGroup) => {
    setActiveGroup(group);
    setIsEditing(true);
  };
  const onColumnDragEnd = ({ active, over }: DragEndEvent) => {
    if (!over || active.id === over.id) return;
    setColumnOrder((current) =>
      arrayMove(
        current,
        current.indexOf(String(active.id)),
        current.indexOf(String(over.id)),
      ),
    );
  };
  return (
    <>
      <MattersStyleTableShell
        table={table}
        columnOrder={columnOrder}
        columnIdCountBaseline={columnIds.length}
        onColumnDragEnd={onColumnDragEnd}
        sensors={sensors}
        search={{
          value: search,
          onChange: setSearch,
          placeholder: "Search permission groups...",
        }}
        isLoading={isLoading}
        isError={isError}
        errorMessage={serverErrorFallbackText}
        emptyMessage="No permission groups found."
        totalFilteredRowCount={rows.length}
        entityNamePlural="permission groups"
        sorting={sorting}
        tableProps={{ "data-testid": tanstackTableNames.userGroups }}
        getTableRowProps={(row) => ({
          className: "cursor-pointer",
          onClick: () => {
            openGroup(row.original);
          },
        })}
      />
      <PermissionGroupModal
        open={isAdding || isEditing}
        setOpen={(open) => {
          if (!open) {
            setIsAdding(false);
            setIsEditing(false);
            setActiveGroup(null);
          }
        }}
        group={isEditing ? (activeGroup ?? undefined) : undefined}
        catalog={catalog}
        isReadOnly={isEditing && !canUpdate}
        canDelete={canDelete}
        onDelete={(group) => {
          setPendingDelete(group);
        }}
      />
      <ActionConfirmationModal
        open={Boolean(pendingDelete)}
        setOpen={(open) => {
          if (!open) setPendingDelete(null);
        }}
        title="Delete this permission group?"
        description="This action cannot be undone."
        buttonText="Delete"
        afterActionFn={async () => {
          if (pendingDelete) await deleteGroup.mutateAsync(pendingDelete.id);
          setPendingDelete(null);
        }}
      />
    </>
  );
}
function PermissionGroupModal({
  open,
  setOpen,
  group,
  catalog,
  isReadOnly,
  canDelete,
  onDelete,
}: {
  open: boolean;
  setOpen: (open: boolean) => void;
  group?: PermissionGroup;
  catalog: PermissionCatalogDefinition[];
  isReadOnly: boolean;
  canDelete: boolean;
  onDelete: (group: PermissionGroup) => void;
}) {
  const createGroup = useCreatePermissionGroup();
  const updateGroup = useUpdatePermissionGroup();
  const [activeTab, setActiveTab] = useState("permissions");
  const lastResetGroupId = useRef<string | null>(null);
  const form = useForm<FormValues>({
    resolver: zodResolver(formSchema),
    defaultValues: {
      label: group?.label ?? "",
      description: group?.description ?? "",
      grants: normalizeInferredPageGrants(group?.grants ?? [], catalog),
    },
  });
  const { reset } = form;
  useEffect(() => {
    if (!open) {
      lastResetGroupId.current = null;
      return;
    }

    const groupId = group?.id ?? "new";
    if (lastResetGroupId.current === groupId && form.formState.isDirty) return;

    reset({
      label: group?.label ?? "",
      description: group?.description ?? "",
      grants: normalizeInferredPageGrants(group?.grants ?? [], catalog),
    });
    lastResetGroupId.current = groupId;
    setActiveTab("permissions");
  }, [catalog, form.formState.isDirty, group, open, reset]);
  const onSubmit = async (data: FormValues) => {
    const payload = {
      label: data.label,
      description: data.description || null,
      grants: normalizeInferredPageGrants(data.grants, catalog),
    };
    if (group) {
      await updateGroup.mutateAsync({
        ...payload,
        id: group.id,
        dateModified: group.dateModified,
      });
    } else {
      await createGroup.mutateAsync(payload);
    }
    setOpen(false);
  };
  const setPermissionActions = (
    permissionId: string,
    actions: PermissionGroupAction[],
    isNotification: boolean,
  ) => {
    const grants = form.getValues("grants");
    const resolvedActions = isNotification
      ? actions.filter(isNotificationAction).slice(-1)
      : actions.filter((action) => !isNotificationAction(action));
    const nextGrants = [
      ...grants.filter(
        (grant) =>
          grant.permissionId !== permissionId ||
          isNotificationAction(grant.action) !== isNotification,
      ),
      ...resolvedActions.map((action) => ({ permissionId, action })),
    ];
    form.setValue(
      "grants",
      isNotification
        ? nextGrants
        : normalizeInferredPageGrants(nextGrants, catalog),
      { shouldDirty: true, shouldValidate: form.formState.isSubmitted },
    );
  };
  const grants = form.watch("grants");
  const isSaving = createGroup.isPending || updateGroup.isPending;
  const permissionCatalog = catalog
    .map((permission) => ({
      ...permission,
      actions: permission.actions.filter(
        ({ action }) => !action.startsWith("notify."),
      ),
    }))
    .filter((permission) => permission.actions.length > 0);
  const notificationCatalog = catalog
    .map((permission) => ({
      ...permission,
      actions: permission.actions.filter(({ action }) =>
        action.startsWith("notify."),
      ),
    }))
    .filter((permission) => permission.actions.length > 0);
  const pagePermissionCatalog = permissionCatalog.filter(
    (permission) => permission.kind === "page",
  );
  const objectPermissionCatalog = permissionCatalog.filter(
    (permission) => permission.kind !== "page",
  );
  return (
    <Form {...form}>
      <AppModal
        open={open}
        onOpenChange={setOpen}
        isDirty={form.formState.isDirty}
        title={
          isReadOnly
            ? "View permission group"
            : group
              ? "Update permission group"
              : "Create permission group"
        }
        description="Configure the permissions granted to members of this group."
        size="3xl"
        loading={isSaving}
        footer={
          <div className="flex w-full flex-wrap justify-between gap-2">
            {form.formState.errors.grants ? (
              <p role="alert" className="text-destructive w-full text-sm">
                Some selected permissions are invalid. Review your selections
                and try again.
              </p>
            ) : null}
            {group && canDelete && !isReadOnly ? (
              <Button
                variant="destructive"
                type="button"
                onClick={() => {
                  onDelete(group);
                }}
              >
                Delete
              </Button>
            ) : (
              <span />
            )}
            <div className="flex gap-2">
              <FormCancelButton
                label={isReadOnly ? "Close" : "Cancel"}
                setOpen={setOpen}
                isDirty={form.formState.isDirty}
              />
              {!isReadOnly ? (
                <FormSubmitButton submitFormId={FORM_ID} loading={isSaving} />
              ) : null}
            </div>
          </div>
        }
      >
        <form
          id={FORM_ID}
          onSubmit={form.handleSubmit(onSubmit)}
          className="flex min-h-0 grow flex-col overflow-hidden"
        >
          <fieldset
            disabled={isReadOnly}
            className="space-y-5 overflow-y-auto p-6"
          >
            <TextFormField label="Label*" register={form.register("label")} />
            <label className="grid gap-1">
              <Text variant="body">Description</Text>
              <Textarea {...form.register("description")} />
            </label>
            <Tabs value={activeTab} onValueChange={setActiveTab}>
              <TabsList>
                <TabsTrigger value="permissions">Permissions</TabsTrigger>
                <TabsTrigger value="notifications">Notifications</TabsTrigger>
              </TabsList>
              <TabsContent value="permissions" className="space-y-4 pt-4">
                <Accordion type="multiple" defaultValue={["pages", "objects"]}>
                  <PermissionCatalogSection
                    value="pages"
                    title="Pages"
                    catalog={pagePermissionCatalog}
                    grants={grants}
                    onActionsChange={setPermissionActions}
                  />
                  <PermissionCatalogSection
                    value="objects"
                    title="Actions"
                    catalog={objectPermissionCatalog}
                    grants={grants}
                    onActionsChange={setPermissionActions}
                  />
                </Accordion>
              </TabsContent>
              <TabsContent value="notifications" className="space-y-4 pt-4">
                {notificationCatalog.length ? (
                  <NotificationCatalogBulkActions
                    catalog={notificationCatalog}
                    onActionsChange={setPermissionActions}
                  />
                ) : null}
                <PermissionCatalogGrants
                  catalog={notificationCatalog}
                  grants={grants}
                  onActionsChange={setPermissionActions}
                />
              </TabsContent>
            </Tabs>
          </fieldset>
        </form>
      </AppModal>
    </Form>
  );
}
